import { Address, ServiceResponse } from '@bzbs/react-api-client';
import { create } from 'zustand';
import { useBuzzebeesAppStore } from './buzzebeess-app.store';

export type AddressState = {
  addresses: Address[];
  taxAddresses: Address[];
  selectedAddress: Address | null;
  selectedTaxAddress: Address | null;
  isLoading: boolean;
  error: ServiceResponse<unknown> | null;
};

export type AddressActions = {
  setSelectedAddress: (address: Address | null) => void;
  fetchAddresses: () => Promise<ServiceResponse<Address[]>>;
  addAddress: (address: AddressFormData) => Promise<ServiceResponse<Address>>;
  editAddress: (address: AddressFormData) => Promise<ServiceResponse<Address>>;
  deleteAddress: (rowKey: string) => Promise<ServiceResponse<unknown>>;
  setSelectedTaxAddress: (address: Address | null) => void;
  fetchTaxAddresses: () => Promise<ServiceResponse<Address[]>>;
  addTaxAddress: (address: AddressFormData) => Promise<ServiceResponse<Address>>;
  editTaxAddress: (address: AddressFormData) => Promise<ServiceResponse<Address>>;
  deleteTaxAddress: (rowKey: string) => Promise<ServiceResponse<unknown>>;
  clear: () => void;
};

export type AddressStore = AddressState & AddressActions;

export type PersonType = 'individual' | 'company';

export type AddressFormData = {
  addressName?: string;
  firstName?: string;
  lastName?: string;
  address?: string;
  zipcode?: string;
  provinceCode?: string;
  provinceName?: string;
  districtCode?: string;
  districtName?: string;
  subDistrictCode?: string;
  subDistrictName?: string;
  contactNumber?: string;
  countryCode?: string;
  countryName?: string;
  isDefault?: boolean;
  rowKey?: string;
  // Tax address specific fields
  taxId?: string;
  personType?: PersonType;
  companyName?: string;
};

export const useAddressStore = create<AddressStore>((set, get) => ({
  addresses: [],
  taxAddresses: [],
  selectedAddress: null,
  selectedTaxAddress: null,
  isLoading: false,
  error: null,

  setSelectedAddress: (address: Address | null) => set({ selectedAddress: address }),

  fetchAddresses: async () => {
    const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

    if (!bzbsService) {
      return { type: 'client-error', message: 'Service not configured' };
    }

    set({ isLoading: true });
    const response = await bzbsService.addressApi.userAddresses();
    set({ isLoading: false });

    if (response.type === 'success') {
      set({
        addresses: response.model,
        error: null,
      });
    } else {
      set({ error: response });
    }

    return response;
  },

  addAddress: async (addressForm: AddressFormData) => {
    const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

    if (!bzbsService) {
      return { type: 'client-error', message: 'Service not configured' };
    }

    set({ isLoading: true });
    const response = await bzbsService.addressApi.updateAddress({
      ...addressForm,
      rowKey: '',
    });
    set({ isLoading: false });

    if (response.type === 'success') {
      get().fetchAddresses();
      set({ error: null });
    } else {
      set({ error: response });
    }

    return response;
  },

  editAddress: async (addressForm: AddressFormData) => {
    const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

    if (!bzbsService) {
      return { type: 'client-error', message: 'Service not configured' };
    }

    set({ isLoading: true });
    const response = await bzbsService.addressApi.updateAddress({
      ...addressForm,
    });
    set({ isLoading: false });

    if (response.type === 'success') {
      get().fetchAddresses();
      set({ error: null });
    } else {
      set({ error: response });
    }

    return response;
  },

  deleteAddress: async (rowKey: string) => {
    const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

    if (!bzbsService) {
      return { type: 'client-error', message: 'Service not configured' };
    }

    set({ isLoading: true });
    const response = await bzbsService.addressApi.deleteAddress({ rowKey });
    set({ isLoading: false });

    if (response.type === 'success') {
      get().fetchAddresses();
      set({ error: null });
    } else {
      set({ error: response });
    }

    return response;
  },

  setSelectedTaxAddress: (address: Address | null) => set({ selectedTaxAddress: address }),

  fetchTaxAddresses: async () => {
    const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

    if (!bzbsService) {
      return { type: 'client-error', message: 'Service not configured' };
    }

    set({ isLoading: true });
    const response = await bzbsService.addressApi.userTaxAddresses();
    set({ isLoading: false });

    if (response.type === 'success') {
      set({
        taxAddresses: response.model,
        error: null,
      });
    } else {
      set({ error: response });
    }

    return response;
  },

  addTaxAddress: async (addressForm: AddressFormData) => {
    const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

    if (!bzbsService) {
      return { type: 'client-error', message: 'Service not configured' };
    }

    set({ isLoading: true });
    const response = await bzbsService.addressApi.updateTaxAddress({
      ...addressForm,
      rowKey: '',
    });
    set({ isLoading: false });

    if (response.type === 'success') {
      get().fetchTaxAddresses();
      set({ error: null });
    } else {
      set({ error: response });
    }

    return response;
  },

  editTaxAddress: async (addressForm: AddressFormData) => {
    const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

    if (!bzbsService) {
      return { type: 'client-error', message: 'Service not configured' };
    }

    set({ isLoading: true });
    const response = await bzbsService.addressApi.updateTaxAddress({
      ...addressForm,
      rowKey: addressForm.rowKey || '',
    });
    set({ isLoading: false });

    if (response.type === 'success') {
      get().fetchTaxAddresses();
      set({ error: null });
    } else {
      set({ error: response });
    }

    return response;
  },

  deleteTaxAddress: async (rowKey: string) => {
    const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

    if (!bzbsService) {
      return { type: 'client-error', message: 'Service not configured' };
    }

    set({ isLoading: true });
    const response = await bzbsService.addressApi.deleteTaxAddress({ rowKey });
    set({ isLoading: false });

    if (response.type === 'success') {
      get().fetchTaxAddresses();
      set({ error: null });
    } else {
      set({ error: response });
    }

    return response;
  },

  clear: () => {
    set({
      addresses: [],
      taxAddresses: [],
      selectedAddress: null,
      selectedTaxAddress: null,
      isLoading: false,
      error: null,
    });
  },
}));
